import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Package, Search, AlertTriangle } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  sku: string;
  baselinker_id: string;
  stockQuantity: number;
  mainImage: string;
}

type StockLevel = 'all' | 'low' | 'medium' | 'good' | 'nostock';

const STOCK_THRESHOLDS = {
  nostock: 0,   // Sem stock
  low: 10,     // Abaixo de 10 = baixo
  medium: 20   // Abaixo de 20 = médio, acima = bom
};

export default function Stock() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState<StockLevel>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { currentUser } = useAuth();
  const itemsPerPage = 25;

  useEffect(() => {
    loadProducts();
  }, [currentUser]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsRef = collection(db, 'products');
      
      // Primeiro obtém os produtos do utilizador logado
      const userProductsQuery = query(
        productsRef,
        where('companyId', '==', currentUser?.companyId)
      );
      
      const userProductsSnapshot = await getDocs(userProductsQuery);
      const userBaselinkerIds = userProductsSnapshot.docs
        .map(doc => doc.data().baselinker_id)
        .filter(id => id); // Filtra valores null/undefined/empty
      
      let allProducts: Product[] = [];
      
      // Se não houver IDs, retorna lista vazia
      if (userBaselinkerIds.length === 0) {
        setProducts([]);
        setError('');
        setLoading(false);
        return;
      }

      // Divide os IDs em grupos de 30 (limite do Firestore)
      for (let i = 0; i < userBaselinkerIds.length; i += 30) {
        const batchIds = userBaselinkerIds.slice(i, i + 30);
        const q = query(
          productsRef,
          where('baselinker_id', 'in', batchIds)
        );
        
        const snapshot = await getDocs(q);
        const batchProducts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Product[];
        
        allProducts = [...allProducts, ...batchProducts];
      }

      setProducts(allProducts);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setError('Erro ao carregar produtos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getStockLevel = (quantity: number): StockLevel => {
    if (quantity <= STOCK_THRESHOLDS.nostock) return 'nostock';
    if (quantity < STOCK_THRESHOLDS.low) return 'low';
    if (quantity < STOCK_THRESHOLDS.medium) return 'medium';
    return 'good';
  };

  const getStockColor = (level: StockLevel): string => {
    switch (level) {
      case 'nostock':
        return 'bg-gray-100 text-gray-800';
      case 'low':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'good':
        return 'bg-green-100 text-green-800';
      default:
        return '';
    }
  };

  const getStockLabel = (level: StockLevel): string => {
    switch (level) {
      case 'nostock':
        return 'Sem Stock';
      case 'low':
        return 'Baixo';
      case 'medium':
        return 'Médio';
      case 'good':
        return 'Bom';
      default:
        return '';
    }
  };

  const filteredProducts = products.filter(product => {
    // Primeiro filtra por nível de stock
    if (stockFilter !== 'all') {
      const level = getStockLevel(product.stockQuantity);
      if (level !== stockFilter) return false;
    }

    // Depois filtra por termo de busca
    return (
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.baselinker_id?.includes(searchTerm)
    );
  });

  // Contagem de produtos por nível de stock
  const stockCounts = products.reduce((acc, product) => {
    const level = getStockLevel(product.stockQuantity);
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {} as Record<StockLevel, number>);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Package className="text-primary" size={24} />
        <h1 className="text-2xl font-bold text-secondary">Stock Fulfillment</h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Stock Level Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        {/* Resumo do Stock */}
        <div className="flex gap-4 w-full bg-white p-4 rounded-lg shadow">
          <div className="flex-1 flex items-center gap-2 p-3 rounded-lg bg-gray-50">
            <Package className="text-gray-500" size={20} />
            <div>
              <div className="text-sm text-gray-500">Sem Stock</div>
              <div className="text-lg font-semibold text-gray-600">{stockCounts.nostock || 0} produtos</div>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-2 p-3 rounded-lg bg-red-50">
            <AlertTriangle className="text-red-500" size={20} />
            <div>
              <div className="text-sm text-gray-500">Stock Baixo</div>
              <div className="text-lg font-semibold text-red-600">{stockCounts.low || 0} produtos</div>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-2 p-3 rounded-lg bg-yellow-50">
            <AlertTriangle className="text-yellow-500" size={20} />
            <div>
              <div className="text-sm text-gray-500">Stock Médio</div>
              <div className="text-lg font-semibold text-yellow-600">{stockCounts.medium || 0} produtos</div>
            </div>
          </div>
          <div className="flex-1 flex items-center gap-2 p-3 rounded-lg bg-green-50">
            <Package className="text-green-500" size={20} />
            <div>
              <div className="text-sm text-gray-500">Stock Bom</div>
              <div className="text-lg font-semibold text-green-600">{stockCounts.good || 0} produtos</div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-2 w-full">
          <button
            onClick={() => setStockFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              stockFilter === 'all'
                ? 'bg-primary text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setStockFilter('nostock')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              stockFilter === 'nostock'
                ? 'bg-gray-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Sem Stock
          </button>
          <button
            onClick={() => setStockFilter('low')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              stockFilter === 'low'
                ? 'bg-red-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Stock Baixo
          </button>
          <button
            onClick={() => setStockFilter('medium')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              stockFilter === 'medium'
                ? 'bg-yellow-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Stock Médio
          </button>
          <button
            onClick={() => setStockFilter('good')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              stockFilter === 'good'
                ? 'bg-green-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Stock Bom
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Pesquisar por nome, SKU ou ID Baselinker..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
          />
          <Search 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
            size={20} 
          />
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Produto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SKU
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID Baselinker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts.length > 0 ? (
              currentItems.map((product) => {
                const stockLevel = getStockLevel(product.stockQuantity);
                return (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {product.mainImage ? (
                          <img 
                            src={product.mainImage}
                            alt={product.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Package size={20} className="text-gray-500" />
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {product.name || 'Sem nome'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.sku || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.baselinker_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          getStockColor(stockLevel)
                        }`}>
                          {product.stockQuantity || 0}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({getStockLabel(stockLevel)})
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                  {searchTerm 
                    ? 'Nenhum produto encontrado com os critérios de pesquisa.'
                    : 'Nenhum produto com ID Baselinker encontrado.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {filteredProducts.length > itemsPerPage && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredProducts.length)} de {filteredProducts.length} produtos
              </div>
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    className={`px-3 py-1 text-sm rounded-md ${
                      currentPage === i + 1
                        ? 'bg-primary text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
